import React from 'react';

import {Anchor, IconButton} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {useGoogleClassroomSync} from 'client/components/ContentDiscovery/Document/AssignmentSharing/useGoogleClassroomSync';
import {withStopPropagation} from 'client/components/ContentDiscovery/Document/AssignmentSharing/utils';

import {SyncStatusOverlay} from '../lmsSync/SyncStatusOverlay';
import {invalidateClassGoogleClassroomSync} from './invalidateClassGoogleClassroomSync';

const CONNECT_MESSAGE = 'Connect Google Classrooms to sync this assignment.';

const GC_SYNC_ICON: IconProp = ['fab', 'google'];

const GC_SYNC_ICON_BUTTON_PROPS = {
  icon: GC_SYNC_ICON,
  size: 's' as const,
  tooltipProps: {theme: 'dark', placement: 'bottom', showArrow: true} as const
};

interface Props {
  assignmentId: string;
  classroomId: string;
  /** Class is linked to a Google Classroom course; false → render the connect prompt. */
  classroomConnectedToGoogleClassroom: boolean;
  /** The teacher's Google Classroom connection per the backend; `undefined` while unknown. */
  googleClassroomConnected?: boolean;
  /** Seeds the initial synced state (useGoogleClassroomSync also follows later prop changes). */
  initialSynced: boolean;
}

/**
 * Per-assignment Google Classroom sync affordance for the v2 (class-scoped) assignments row —
 * mirrors AssignmentLtiSyncCell's stateful icon, reusing the builder's useGoogleClassroomSync hook
 * so the sync flow (including the not-connected connect prompt) is unchanged. A synced assignment
 * shows a check badge that swaps to the rotate badge on hover; clicking it syncs again.
 */
export const AssignmentGoogleClassroomSyncCell = ({
  assignmentId,
  classroomId,
  classroomConnectedToGoogleClassroom,
  googleClassroomConnected,
  initialSynced
}: Props) => {
  const {isSynced, isRequestPending, needsReconnect, sync, reconnect} = useGoogleClassroomSync(
    assignmentId,
    classroomId,
    initialSynced,
    googleClassroomConnected
  );

  const syncAndRefresh = () => sync().then(() => invalidateClassGoogleClassroomSync(classroomId));

  if (!classroomConnectedToGoogleClassroom || needsReconnect) {
    // Reads as disabled, but the icon and the tooltip's "Connect now" both start the connect flow so
    // keyboard users (who cannot reach the portaled tooltip link) still have a way in.
    return (
      <IconButton
        {...GC_SYNC_ICON_BUTTON_PROPS}
        label='Connect Google Classroom to sync this assignment'
        className='relative opacity-50'
        onClick={withStopPropagation(reconnect)}
        tooltip={
          <div>
            {`${CONNECT_MESSAGE} `}
            <Anchor
              as='button'
              type='button'
              inheritColor
              underlined
              data-testid='google-classroom-reconnect-button'
              onClick={reconnect}
            >
              Connect now.
            </Anchor>
          </div>
        }
        tooltipProps={{theme: 'dark', placement: 'bottom', showArrow: true, interactive: true}}
      >
        <SyncStatusOverlay status='sync' />
      </IconButton>
    );
  }

  if (isSynced) {
    return (
      <IconButton
        {...GC_SYNC_ICON_BUTTON_PROPS}
        label='Synced with Google Classroom. Resync assignment'
        className='group relative'
        disabled={isRequestPending}
        onClick={withStopPropagation(syncAndRefresh)}
        tooltip='Resync assignment'
        iconProps={{spin: isRequestPending}}
      >
        <SyncStatusOverlay status='syncedResyncOnHover' />
      </IconButton>
    );
  }

  return (
    <IconButton
      {...GC_SYNC_ICON_BUTTON_PROPS}
      label='Sync assignment'
      className='relative'
      disabled={isRequestPending}
      onClick={withStopPropagation(syncAndRefresh)}
      tooltip='Sync assignment'
      iconProps={{spin: isRequestPending}}
    >
      <SyncStatusOverlay status='sync' />
    </IconButton>
  );
};

/**
 * Cross-class (All Classwork) variant: sync is per-class only, so this is a non-actionable icon
 * whose tooltip points the teacher to the per-class flow. Mirrors CrossClassLtiSyncCell. Shown only
 * when ≥1 class is linked to a Google Classroom course.
 */
export const CrossClassGoogleClassroomSyncCell = () => (
  // Informational only — rendered as a labelled `img` (not a button) so it's out of the tab order and
  // AT announces the guidance instead of a no-op command. Sync is per-class.
  <IconButton
    {...GC_SYNC_ICON_BUTTON_PROPS}
    as='span'
    role='img'
    label='Google Classroom sync is available per class'
    tooltip='To sync with Google Classroom, navigate to an individual class or open the assignment editor.'
  />
);
